import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WhatsAppTemplateSelect } from '@/components/ui/whatsapp-template-select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { settingsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Save, Package, Calendar, Clock, Plug, Shield, Settings as SettingsIcon, Plus, Edit, Trash2, Tag, Mail, MessageCircle, CreditCard, FileText, Zap, BarChart3, Bot, Upload, Link, Globe, Sparkles, AlertTriangle, RefreshCw, ClipboardList, Database, Download, History, Gift, Loader2, Euro, Utensils, Scan, Smartphone, Newspaper } from 'lucide-react';
import axios from 'axios';
import NotificationTemplates from '@/components/NotificationTemplates';import AnamneseSettings from '@/components/AnamneseSettings';
import PortalSettings from '@/components/PortalSettings';
import AIApiSettings from '@/components/settings/AIApiSettings';
import LegalSettingsCard from '@/components/admin/LegalSettingsCard';
import MealDistributionCard from '@/components/admin/MealDistributionCard';
import NutritionCalculationSettings from '@/components/admin/NutritionCalculationSettings';
import InBodySettingsCard from '@/components/admin/InBodySettingsCard';
// Extracted Settings Components
import BackupSettings from '@/components/settings/BackupSettings';
import MaintenanceSettings from '@/components/settings/MaintenanceSettings';
import { SettingsSyncCard } from '@/components/settings/SettingsSyncCard';
import AdminBackups from '@/components/admin/AdminBackups';
import PeriodExportCard from '@/components/settings/PeriodExportCard';
import Tarif595Settings from '@/components/settings/Tarif595Settings';
import CalendarSettings from '@/components/settings/CalendarSettings';
import SystemSettings from '@/components/settings/SystemSettings';
import InvoiceSettings from '@/components/settings/InvoiceSettings';
import AvailabilitySettings from '@/components/settings/AvailabilitySettings';
import PaymentColorsCard from '@/components/settings/PaymentColorsCard';
import ReviewAutomationCard from '@/components/settings/ReviewAutomationCard';
import FeedbackSettingsCard from '@/components/settings/FeedbackSettingsCard';
import PaymentRemindersSettings from '@/components/settings/PaymentRemindersSettings';
import TrialFollowupSettings from '@/components/settings/TrialFollowupSettings';
import ReservationSettings from '@/components/settings/ReservationSettings';
import ScannerSettings from '@/components/settings/ScannerSettings';
// New extracted components
import AddonsSettings from '@/components/settings/AddonsSettings';
import CategoriesSettings from '@/components/settings/CategoriesSettings';
import TaxSettings from '@/components/settings/TaxSettings';
// Extracted tab components
import { NewsletterTab } from '@/components/settings/tabs';
// Newly extracted components (Refactoring)
import GeneralSettingsForm from '@/components/settings/GeneralSettingsForm';
import SchedulerSettingsCard from '@/components/settings/SchedulerSettingsCard';
import ReportEmailSettings from '@/components/settings/ReportEmailSettings';
import BirthdaySettings from '@/components/settings/BirthdaySettings';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_VERSION = "2.1.0";

// GeneralSettingsForm und SchedulerSettingsCard sind jetzt in separaten Dateien:
// - /components/settings/GeneralSettingsForm.jsx
// - /components/settings/SchedulerSettingsCard.jsx

const SettingsPage = ({ embedded = false, forcedCard = null, forcedTab = null }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { companyName, name: brandName } = useTenantBranding();
  const businessName = companyName || brandName || 'Ihr Betrieb';
  const tabFromUrl = forcedTab || searchParams.get('tab');
  const cardFromUrl = searchParams.get('card');
  
  // Determine initial states based on URL params
  // Phase 3: "ki-api" ist in KI / Kanäle / Zahlungen aufgeteilt -> subtab-bewusst umleiten
  const resolveKiApiGroup = () => {
    const sub = searchParams.get('subtab');
    if (sub === 'smtp' || sub === 'whatsapp') return 'kanaele';
    return 'ki';
  };
  const getInitialCard = () => {
    if (forcedCard === 'ki-api') return resolveKiApiGroup();
    if (forcedCard === 'colors') return 'availability';
    if (forcedCard) return forcedCard;
    if (tabFromUrl === 'reminders' || tabFromUrl === 'notification_templates' || tabFromUrl === 'birthday' || tabFromUrl === 'newsletter') {
      return 'communication';
    }
    if (cardFromUrl === 'ki-api' || tabFromUrl === 'ki-api') return resolveKiApiGroup();
    if (cardFromUrl === 'colors' || tabFromUrl === 'colors') return 'availability';
    if (cardFromUrl) return cardFromUrl;
    if (tabFromUrl) return tabFromUrl;
    return null;
  };
  
  const getInitialCommTab = () => {
    if (tabFromUrl === 'reminders' || tabFromUrl === 'notification_templates' || tabFromUrl === 'birthday' || tabFromUrl === 'newsletter') {
      return tabFromUrl;
    }
    return null;
  };
  
  // Function to go back to main settings hub
  const goBackToSettingsHub = () => navigate('/settings');
  
  const [apiSettings, setApiSettings] = useState({
    // Payrexx
    payrexx_instance_name: '',
    payrexx_api_secret: '',
    payrexx_webhook_secret: '',
    payrexx_webhook_url: '',
    // Payrexx Redirect URLs
    payrexx_success_url: '',
    payrexx_failed_url: '',
    payrexx_cancel_url: '',
    // Communication
    gmail_api_key: '',
    whatsapp_webhook_url: '',
    // InBody
    inbody_enabled: false,
    inbody_portal_visible: false,
    inbody_api_url: 'https://apiind.lookinbody.com',
    inbody_account: '',
    inbody_api_key: '',
    inbody_webhook_secret: ''
  });
  
  const [billingAutomation, setBillingAutomation] = useState({
    enabled: false,
    billing_day: 30,
    last_run: null,
    interval_minutes: 60
  });
  
  const [billingLogs, setBillingLogs] = useState([]);
  const [billingPreview, setBillingPreview] = useState(null);
  const [showBillingPreview, setShowBillingPreview] = useState(false);
  const [loadingBillingPreview, setLoadingBillingPreview] = useState(false);
  const [runningBilling, setRunningBilling] = useState(false);
  
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    enabled: false
  });
  
  const [calendarSettings, setCalendarSettings] = useState({
    slot_duration: '30',
    booking_window_days: '60',
    cancellation_hours: '24',
    no_show_penalty_type: 'full',
    no_show_penalty_value: '0',
    calendar_slot_height: 24,
    calendar_start_hour: 6,   // NEU: Kalender-Startzeit
    calendar_end_hour: 20     // NEU: Kalender-Endzeit
  });
  
  const [paymentColors, setPaymentColors] = useState({
    pending: '#FCD34D',  // Yellow
    paid: '#10B981',     // Green
    overdue: '#EF4444'   // Red
  });
  
  const [availability, setAvailability] = useState({
    monday: { active: true, slots: [{ from: '09:00', to: '18:00' }] },
    tuesday: { active: true, slots: [{ from: '09:00', to: '18:00' }] },
    wednesday: { active: true, slots: [{ from: '09:00', to: '18:00' }] },
    thursday: { active: true, slots: [{ from: '09:00', to: '18:00' }] },
    friday: { active: true, slots: [{ from: '09:00', to: '18:00' }] },
    saturday: { active: false, slots: [{ from: '10:00', to: '14:00' }] },
    sunday: { active: false, slots: [{ from: '10:00', to: '14:00' }] }
  });
  
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  
  const [taxSettings, setTaxSettings] = useState({
    default_tax_rate: 8.1,
    tax_included_in_prices: true,
    show_tax_breakdown: true,
    tax_number: '',
    // Invoice & PDF spezifisch
    company_name: '',
    company_street: '',
    company_postal_code: '',
    company_city: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    invoice_email_subject: '',
    invoice_email_body: '',
    payment_link_email_subject: '',
    payment_link_email_body_html: '',
    pdf_primary_color: '#10b981',
    footer_text: 'Vielen Dank für Ihr Vertrauen.',
    pdf_greeting_text: 'Guten Tag {customer_name},',
    pdf_closing_text: 'Freundliche Grüsse\n{company_name}',
    logo_url: '',
    payment_terms_days: 30,
    bank_iban: '',
    bank_account_holder: '',
    bank_name: '',
    bank_bic: '',
    payment_notes: ''
  });
  
  const [reminderSettings, setReminderSettings] = useState({
    enabled: true,
    first_reminder_days: 7,
    second_reminder_days: 14,
    third_reminder_days: null,
    max_reminders: 2,
    send_email: true,
    send_whatsapp: false,
    first_reminder_template_id: 'invoice_reminder',
    second_reminder_template_id: 'invoice_reminder',
    third_reminder_template_id: null
  });
  
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [payrexxStatus, setPayrexxStatus] = useState({ mock_mode: true, has_api_key: false, instance_name: '' });
  const [testingPayrexx, setTestingPayrexx] = useState(false);
  const [activeSettingCard, setActiveSettingCard] = useState(() => getInitialCard()); // Für Karten-Navigation
  const [activeCommunicationTab, setActiveCommunicationTab] = useState(() => getInitialCommTab()); // Für Kommunikations-Sub-Navigation
  const [whatsappTemplates, setWhatsappTemplates] = useState([]); // WhatsApp Templates
  // testEmail state moved to AIApiSettings component
  
  // Backup Settings State
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    interval: 'daily',
    include_json: true,
    include_csv: true,
    backup_hour: 3
  });
  const [backupLogs, setBackupLogs] = useState([]);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);
  
  // Effect to update activeSettingCard when URL params change
  useEffect(() => {
    const newCard = getInitialCard();
    if (newCard && newCard !== activeSettingCard) {
      setActiveSettingCard(newCard);
    }
    const newCommTab = getInitialCommTab();
    if (newCommTab && newCommTab !== activeCommunicationTab) {
      setActiveCommunicationTab(newCommTab);
    }
  }, [tabFromUrl, cardFromUrl, forcedCard]);

  // Add-on Services States
  const [addonSettings, setAddonSettings] = useState({
    enabled: false,
    assistant_trainer_id: null,
    warmup_times: {},
    mappings: []
  });
  const [addonServices, setAddonServices] = useState([]); // Available services for dropdown
  const [addonTrainers, setAddonTrainers] = useState([]); // Available trainers for dropdown
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [savingAddons, setSavingAddons] = useState(false);

  // KI Chatbot States - Moved to AIApiSettings component
  useEffect(() => {
    fetchApiSettings(); // Loads API settings (Payrexx, etc.)
    fetchCalendarSettings();
    fetchAvailabilitySettings();
    fetchPaymentColors(); // NEW
    fetchCategories(); // NEW
    fetchTaxSettings(); // NEW (Invoice & Firmendaten)
    fetchWhatsAppTemplates(); // NEW
    fetchBillingAutomationSettings(); // NEW
    fetchSmtpSettings(); // NEW
    fetchPayrexxStatus();
    fetchReminderSettings(); // NEW
    fetchEmailTemplates(); // NEW
    fetchBackupSettings(); // NEW - Backup
    fetchAddonSettings(); // NEW - Add-on Services
  }, []);

  // Effect to handle URL parameter changes
  useEffect(() => {
    const card = getInitialCard();
    const commTab = getInitialCommTab();
    if (card) setActiveSettingCard(card);
    if (commTab) setActiveCommunicationTab(commTab);
  }, [tabFromUrl, cardFromUrl, forcedCard]);

  // Backup Functions
  const fetchBackupSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/backup/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupSettings({
        enabled: response.data.enabled ?? false,
        interval: response.data.interval ?? 'daily',
        include_json: response.data.include_json ?? true,
        include_csv: response.data.include_csv ?? true,
        backup_hour: response.data.backup_hour ?? 3
      });
      setBackupLogs(response.data.last_backup ? [response.data.last_backup] : []);
    } catch (error) {
      console.error('Fehler beim Laden der Backup-Einstellungen', error);
    }
  };

  const saveBackupSettings = async () => {
    setLoadingBackup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/backup/settings`, backupSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoadingBackup(false);
    }
  };

  const runManualBackup = async (sendEmail = true) => {
    setRunningBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/backup/run`, {
        send_email: sendEmail,
        include_json: backupSettings.include_json,
        include_csv: backupSettings.include_csv
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (sendEmail) {
        toast.success(response.data.message);
      } else {
        // Download backup
        const downloadResponse = await axios.get(`${API}/backup/download/${response.data.download_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `strongfit_backup_${new Date().toISOString().slice(0,10)}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Backup heruntergeladen');
      }
      
      fetchBackupSettings(); // Refresh logs
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Backups');
    } finally {
      setRunningBackup(false);
    }
  };

  // Add-on Services Functions
  const fetchAddonSettings = async () => {
    setLoadingAddons(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch addon settings, services, and trainers in parallel
      const [settingsRes, servicesRes, trainersRes] = await Promise.all([
        axios.get(`${API}/settings/addon-services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAddonSettings({
        enabled: settingsRes.data.enabled ?? false,
        assistant_trainer_id: settingsRes.data.assistant_trainer_id || null,
        warmup_times: settingsRes.data.warmup_times || {},
        mappings: settingsRes.data.mappings || []
      });
      
      // Filter active services
      setAddonServices(servicesRes.data.filter(s => s.active !== false));
      
      // Filter trainers (users with trainer role)
      setAddonTrainers(trainersRes.data.filter(u => u.role === 'trainer' || u.role === 'admin'));
    } catch (error) {
      console.error('Fehler beim Laden der Zusatzleistungen-Einstellungen', error);
    } finally {
      setLoadingAddons(false);
    }
  };

  const saveAddonSettings = async () => {
    setSavingAddons(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/addon-services`, addonSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zusatzleistungen-Einstellungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingAddons(false);
    }
  };

  const addAddonMapping = () => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: [
        ...prev.mappings,
        {
          id: crypto.randomUUID(),
          main_service_id: '',
          addon_service_id: '',
          combo_price: 0,
          is_active: true
        }
      ]
    }));
  };

  const updateAddonMapping = (mappingId, field, value) => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: prev.mappings.map(m => 
        m.id === mappingId ? { ...m, [field]: value } : m
      )
    }));
  };

  const removeAddonMapping = (mappingId) => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: prev.mappings.filter(m => m.id !== mappingId)
    }));
  };

  const updateWarmupTime = (serviceId, minutes) => {
    setAddonSettings(prev => ({
      ...prev,
      warmup_times: {
        ...prev.warmup_times,
        [serviceId]: parseInt(minutes) || 0
      }
    }));
  };

  const fetchPaymentColors = async () => {
    try {
      const response = await axios.get(`${API}/settings/payment-colors`);
      if (response.data) {
        // Nur die relevanten Felder übernehmen, Standardwerte behalten wenn nicht vorhanden
        setPaymentColors(prev => ({
          pending: response.data.pending || prev.pending,
          paid: response.data.paid || prev.paid,
          overdue: response.data.overdue || prev.overdue
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Farb-Einstellungen', error);
      // Bei Fehler Standardwerte beibehalten
    }
  };

  const fetchBillingAutomationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/billing-automation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingAutomation({
        enabled: response.data.enabled ?? false,
        billing_day: response.data.billing_day ?? 30,
        last_run: response.data.last_run ?? null,
        interval_minutes: response.data.interval_minutes ?? 60,
        send_invoice_email: response.data.send_invoice_email ?? true,
      });
      
      // Also fetch logs
      const logsResponse = await axios.get(`${API}/settings/billing-automation/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingLogs(logsResponse.data.logs || []);
    } catch (error) {
      console.error('Fehler beim Laden der Auto-Abrechnungs-Einstellungen', error);
    }
  };
  const fetchPayrexxStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/payrexx/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayrexxStatus(res.data || { mock_mode: true, has_api_key: false, instance_name: '' });
    } catch (error) {
      console.error('Error fetching Payrexx status:', error);
      setPayrexxStatus({ mock_mode: true, has_api_key: false, instance_name: '' });
    }
  };

  const handleTestPayrexx = async () => {
    try {
      setTestingPayrexx(true);
      const token = localStorage.getItem('token');
      
      // First refresh status
      await fetchPayrexxStatus();
      
      // Then test the actual connection
      const response = await axios.post(`${API}/payrexx/test-connection`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
      } else if (response.data.mock_mode) {
        toast.info('⚠️ Payrexx läuft im Mock-Modus (keine API-Credentials konfiguriert)');
      } else {
        toast.error(`❌ ${response.data.error}${response.data.details ? ': ' + response.data.details : ''}`);
      }
    } catch (error) {
      console.error('Error testing Payrexx connection:', error);
      toast.error('Fehler beim Testen der Payrexx-Verbindung');
    } finally {
      setTestingPayrexx(false);
    }
  };

  const [logoFile, setLogoFile] = useState(null);


  const saveBillingAutomationSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/billing-automation?enabled=${billingAutomation.enabled}&billing_day=${billingAutomation.billing_day}&interval_minutes=${billingAutomation.interval_minutes}&send_invoice_email=${billingAutomation.send_invoice_email ?? true}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Auto-Abrechnungs-Einstellungen gespeichert!');
      await fetchBillingAutomationSettings();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      toast.error('Fehler beim Speichern der Einstellungen');
    }
  };

  const handleUploadLogo = async () => {
    const token = localStorage.getItem('token');
    if (!logoFile) {
      toast.error('Bitte zuerst ein Logo auswählen');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      const res = await axios.post(`${API}/invoice-settings/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Logo erfolgreich hochgeladen');
      setTaxSettings((prev) => ({ ...prev, logo_url: res.data.logo_url }));
    } catch (error) {
      console.error('Fehler beim Hochladen des Logos', error);
      toast.error(error?.response?.data?.detail || 'Fehler beim Hochladen des Logos');
    }
  };

  const runBillingNow = async () => {
    // 08/2026: Erst Vorschau zeigen — abgerechnet wird nach Bestätigung
    setLoadingBillingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/settings/billing-automation/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingPreview(data);
      setShowBillingPreview(true);
    } catch (error) {
      console.error('Fehler bei der Vorschau:', error);
      toast.error('Fehler beim Laden der Abrechnungs-Vorschau');
    } finally {
      setLoadingBillingPreview(false);
    }
  };

  const confirmBillingRun = async () => {
    setRunningBilling(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/settings/billing-automation/run-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data?.message || 'Abrechnung gestartet!');
      setShowBillingPreview(false);
      // Ergebnis erscheint asynchron in den Logs — kurz warten und neu laden
      setTimeout(() => fetchBillingAutomationSettings(), 5000);
      await fetchBillingAutomationSettings();
    } catch (error) {
      console.error('Fehler beim Ausführen:', error);
      toast.error('Fehler beim Ausführen der Abrechnung');
    } finally {
      setRunningBilling(false);
    }
  };

  const runNightlyNow = async () => {
    if (!window.confirm('Termin-Abrechnung (Nachtlauf) jetzt starten? Es werden alle offenen, vergangenen Trainings verrechnet – inkl. Zahler-Beziehungen (z.B. für zurückgesetzte Termine). Ende-Monat- & Täglich-Kunden werden ebenfalls berücksichtigt.')) return;
    setRunningBilling(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/settings/billing-automation/run-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data?.message || 'Abrechnung läuft — Ergebnis erscheint unter „Letzte Ausführungen".');
      setTimeout(() => fetchBillingAutomationSettings(), 6000);
    } catch (error) {
      toast.error('Fehler beim Starten der Termin-Abrechnung');
    } finally {
      setRunningBilling(false);
    }
  };

  const restartBillingScheduler = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/settings/billing-automation/restart-scheduler`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || 'Scheduler-Konfiguration aktualisiert');
    } catch (error) {
      console.error('Fehler beim Neustarten des Schedulers', error);
      const msg = error?.response?.data?.detail || 'Fehler beim Neustarten des Schedulers';
      toast.error(msg);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Lade Email-Einstellungen
      const emailResponse = await axios.get(`${API}/email/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Lade Kommunikations-Einstellungen (Erinnerungen, etc.)
      const commResponse = await axios.get(`${API}/communication/reminder-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Kombiniere beide
      setSmtpSettings({
        ...emailResponse.data,
        ...commResponse.data,
        smtp_password: '' // Passwort aus Sicherheitsgründen leer lassen
      });
    } catch (error) {
      console.error('Fehler beim Laden der Einstellungen', error);
    }
  };


  const fetchReminderSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reminder/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Erinnerungs-Einstellungen', error);
    }
  };


  const fetchEmailTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Email-Templates', error);
    }
  };


  const saveReminderSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/reminder/settings`, reminderSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Erinnerungs-Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  // SMTP save/test moved to AIApiSettings component


  const saveCommunicationSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Speichere SMTP Einstellungen
      await axios.put(
        `${API}/email/settings`,
        {
          smtp_host: smtpSettings.smtp_host,
          smtp_port: parseInt(smtpSettings.smtp_port),
          smtp_username: smtpSettings.smtp_username,
          smtp_password: smtpSettings.smtp_password,
          smtp_from_email: smtpSettings.smtp_from_email,
          smtp_from_name: smtpSettings.smtp_from_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Speichere Kommunikations-Einstellungen (Erinnerungen, etc.)
      await axios.put(
        `${API}/communication/reminder-settings`,
        {
          email_reminders_enabled: smtpSettings.email_reminders_enabled || false,
          email_reminder_hours_before: parseInt(smtpSettings.email_reminder_hours || 24),
          email_reminder_mode: smtpSettings.email_reminder_mode || 'before',
          
          whatsapp_reminders_enabled: smtpSettings.whatsapp_reminders_enabled || false,
          whatsapp_reminder_hours_before: parseInt(smtpSettings.whatsapp_reminder_hours || 24),
          whatsapp_reminder_mode: smtpSettings.whatsapp_reminder_mode || 'before',
          whatsapp_reminder_template: smtpSettings.whatsapp_template || '',
          
          email_newsletter_enabled: smtpSettings.email_newsletter_enabled || false,
          whatsapp_newsletter_enabled: smtpSettings.whatsapp_newsletter_enabled || false,
          
          points_warning_enabled: smtpSettings.points_warning_enabled || false,
          points_warning_threshold: parseInt(smtpSettings.points_warning_threshold || 10),
          points_warning_frequency_days: parseInt(smtpSettings.points_warning_frequency_days ?? 7),
          points_warning_max_count: parseInt(smtpSettings.points_warning_max_count ?? 2),
          credit_warning_threshold: parseInt(smtpSettings.credit_warning_threshold ?? 3),
          
          auto_archive_enabled: smtpSettings.auto_archive_enabled !== false,
          auto_archive_hours: parseInt(smtpSettings.auto_archive_hours || 24)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Kommunikations-Einstellungen gespeichert!');
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setLoading(false);
    }
  };


  // ============= KI CHATBOT FUNCTIONS =============
  // Chatbot functions moved to AIApiSettings component

  // Fetch API settings (Payrexx, etc.) from main settings endpoint
  const fetchApiSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiSettings({
        payrexx_instance_name: response.data.payrexx_instance_name || '',
        payrexx_api_secret: response.data.payrexx_api_secret || '',
        payrexx_webhook_secret: response.data.payrexx_webhook_secret || '',
        payrexx_webhook_url: response.data.payrexx_webhook_url || '',
        payrexx_success_url: response.data.payrexx_success_url || '',
        payrexx_failed_url: response.data.payrexx_failed_url || '',
        payrexx_cancel_url: response.data.payrexx_cancel_url || '',
        gmail_api_key: response.data.gmail_api_key || '',
        whatsapp_webhook_url: response.data.whatsapp_webhook_url || '',
        // InBody
        inbody_enabled: response.data.inbody_enabled || false,
        inbody_portal_visible: response.data.inbody_portal_visible || false,
        inbody_api_url: response.data.inbody_api_url || 'https://apiind.lookinbody.com',
        inbody_account: response.data.inbody_account || '',
        inbody_api_key: response.data.inbody_api_key || '',
        inbody_webhook_secret: response.data.inbody_webhook_secret || ''
      });
    } catch (error) {
      console.error('Fehler beim Laden der API-Einstellungen', error);
    }
  };

  const fetchCalendarSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/calendar`);
      setCalendarSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Kalender-Einstellungen', error);
    }
  };

  const fetchAvailabilitySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/availability`);
      const data = response.data;
      
      // Konvertiere API-Format zu Frontend-Format
      // API: { enabled, start, end } -> Frontend: { active, slots: [{from, to}] }
      const convertedData = {};
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      
      days.forEach(day => {
        if (data[day]) {
          // Prüfe ob bereits im neuen Format (mit slots)
          if (data[day].slots) {
            convertedData[day] = data[day];
          } else {
            // Konvertiere vom API-Format
            convertedData[day] = {
              active: data[day].enabled || false,
              slots: [{ from: data[day].start || '09:00', to: data[day].end || '18:00' }]
            };
          }
        } else {
          // Default Werte
          convertedData[day] = { active: false, slots: [{ from: '09:00', to: '18:00' }] };
        }
      });
      
      setAvailability(convertedData);
    } catch (error) {
      console.error('Fehler beim Laden der Verfügbarkeits-Einstellungen', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/service-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien', error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Bitte Kategorie-Namen eingeben');
      return;
    }
    
    try {
      await axios.post(`${API}/service-categories`, 
        { name: newCategoryName.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Kategorie erstellt!');
      setNewCategoryName('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    if (!editCategoryName.trim()) {
      toast.error('Bitte Kategorie-Namen eingeben');
      return;
    }
    
    try {
      await axios.put(
        `${API}/service-categories/${categoryId}`,
        { name: editCategoryName.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Kategorie aktualisiert!');
      setEditingCategory(null);
      setEditCategoryName('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Kategorie &quot;${categoryName}&quot; wirklich löschen?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/service-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Kategorie gelöscht!');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleSaveCalendarSettings = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/calendar`, calendarSettings);
      toast.success('Kalender-Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAvailability = async () => {
    setLoading(true);
    try {
      // Konvertiere Frontend-Format zu API-Format für Kompatibilität
      // Frontend: { active, slots: [{from, to}] } -> API: { enabled, start, end }
      const apiData = {};
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      
      days.forEach(day => {
        const dayData = availability[day] || { active: false, slots: [{ from: '09:00', to: '18:00' }] };
        const slots = dayData.slots || [{ from: '09:00', to: '18:00' }];
        apiData[day] = {
          enabled: dayData.active || false,
          start: slots[0]?.from || '09:00',
          end: slots[0]?.to || '18:00'
        };
      });
      
      await axios.post(`${API}/settings/availability`, apiData);
      toast.success('Öffnungszeiten gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const toggleDayAvailability = (dayKey) => {
    const currentDay = availability[dayKey] || { active: false, slots: [{ from: '09:00', to: '18:00' }] };
    setAvailability({
      ...availability,
      [dayKey]: {
        ...currentDay,
        active: !currentDay.active
      }
    });
  };

  const updateDaySlot = (dayKey, slotIndex, field, value) => {
    const currentDay = availability[dayKey] || { active: false, slots: [{ from: '09:00', to: '18:00' }] };
    const newSlots = [...(currentDay.slots || [{ from: '09:00', to: '18:00' }])];
    newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
    setAvailability({
      ...availability,
      [dayKey]: {
        ...currentDay,
        slots: newSlots
      }
    });
  };

  const getDayKey = (day) => {
    const dayMap = {
      'Montag': 'monday',
      'Dienstag': 'tuesday',
      'Mittwoch': 'wednesday',
      'Donnerstag': 'thursday',
      'Freitag': 'friday',
      'Samstag': 'saturday',
      'Sonntag': 'sunday'
    };
    return dayMap[day];
  };

  // NEW: Tax Settings Functions
  const fetchTaxSettings = async () => {
    try {
      const token = localStorage.getItem('token');

      // Invoice & Firmendaten (zentrale Rechnungs-Settings)
      const invoiceRes = await axios.get(`${API}/invoice-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTaxSettings((prev) => ({
        ...prev,
        ...invoiceRes.data,
        payment_link_email_subject: invoiceRes.data.payment_link_email_subject || prev.payment_link_email_subject,
        payment_link_email_body_html: invoiceRes.data.payment_link_email_body_html || prev.payment_link_email_body_html,
      }));
    } catch (error) {
      console.error('Fehler beim Laden der Steuer-/Invoice-Einstellungen', error);
    }
  };


  const fetchWhatsAppTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter nur genehmigte Templates
      const approved = response.data?.data?.filter(t => t.status === 'APPROVED') || [];
      setWhatsappTemplates(approved);
    } catch (error) {
      console.error('Fehler beim Laden der WhatsApp Templates', error);
    }
  };


  const handleSaveTaxSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/invoice-settings`, taxSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Firmen- & Rechnungs-Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern der Firmen-/Invoice-Einstellungen');
      console.error('Fehler beim Speichern der Firmen-/Invoice-Einstellungen', error);
    } finally {
      setLoading(false);
    }
  };

  // API Settings Functions
  const handleSaveApiSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use /settings endpoint (same as general settings)
      await axios.put(`${API}/settings`, apiSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API-Einstellungen gespeichert!');
      // Refresh Payrexx status after saving
      await fetchPayrexxStatus();
    } catch (error) {
      toast.error('Fehler beim Speichern der API-Einstellungen');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? "w-full" : "max-w-6xl"} data-testid="settings-page">
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <SettingsIcon size={32} />
            Einstellungen
          </h1>
          <p className="text-gray-600 mt-2">Verwalten Sie alle Systemeinstellungen</p>
        </div>
      )}

      {!activeSettingCard ? (
        // Karten-Grid Übersicht
        <div className="space-y-8">
          {/* Allgemeine Einstellungen */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <SettingsIcon size={24} className="text-gray-600" />
              Allgemeine Einstellungen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Allgemein */}
              <div 
                onClick={() => setActiveSettingCard('general')}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <SettingsIcon size={24} className="text-gray-600" />
                  <h3 className="font-semibold text-lg">Allgemein</h3>
                </div>
                <p className="text-sm text-gray-600">Basis-Einstellungen und Studio-Informationen</p>
              </div>

              {/* Kalender */}
              <div 
                onClick={() => setActiveSettingCard('calendar')}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Calendar size={24} className="text-indigo-600" />
                  <h3 className="font-semibold text-lg">Kalender</h3>
                </div>
                <p className="text-sm text-gray-600">Zeitslots, Buchungsfenster, Verfügbarkeit</p>
              </div>

              {/* Öffnungszeiten */}
              <div 
                onClick={() => setActiveSettingCard('availability')}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Clock size={24} className="text-purple-600" />
                  <h3 className="font-semibold text-lg">Öffnungszeiten</h3>
                </div>
                <p className="text-sm text-gray-600">Studio-Verfügbarkeit festlegen</p>
              </div>

              {/* Farben */}
              <div 
                onClick={() => setActiveSettingCard('colors')}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">🎨</span>
                  <h3 className="font-semibold text-lg">Farben</h3>
                </div>
                <p className="text-sm text-gray-600">Farbschema anpassen</p>
              </div>

              {/* Kommunikation (Email + WhatsApp) */}
              <div 
                onClick={() => setActiveSettingCard('communication')}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <MessageCircle size={24} className="text-teal-600" />
                  <h3 className="font-semibold text-lg">Kommunikation</h3>
                </div>
                <p className="text-sm text-gray-600">Email & WhatsApp SMTP, Templates & Erinnerungen</p>
              </div>

              {/* Marketing-Quellen */}
              <div 
                onClick={() => window.location.href = '/settings/referral-sources'}
                className="p-6 border rounded-lg hover:bg-teal-50 cursor-pointer transition-all hover:shadow-md border-teal-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">📣</span>
                  <h3 className="font-semibold text-lg">Marketing-Quellen</h3>
                </div>
                <p className="text-sm text-gray-600">"Wie haben Sie von uns erfahren?" - Optionen</p>
              </div>

              {/* Re-Engagement */}
              <div 
                onClick={() => navigate('/settings/re-engagement')}
                className="p-6 border rounded-lg hover:bg-blue-50 cursor-pointer transition-all hover:shadow-md border-blue-200"
                data-testid="reengagement-settings-card-main"
              >
                <div className="flex items-center gap-3 mb-2">
                  <RefreshCw size={24} className="text-blue-600" />
                  <h3 className="font-semibold text-lg">Re-Engagement</h3>
                </div>
                <p className="text-sm text-gray-600">Inaktive Kunden automatisch erinnern & Rabattcodes</p>
              </div>

              {/* Abandonment-Recovery */}
              <div
                onClick={() => navigate('/settings/recovery')}
                className="p-6 border rounded-lg hover:bg-pink-50 cursor-pointer transition-all hover:shadow-md border-pink-200"
                data-testid="recovery-settings-card-main"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Gift size={24} className="text-pink-600" />
                  <h3 className="font-semibold text-lg">Abandonment-Recovery</h3>
                </div>
                <p className="text-sm text-gray-600">Neukunden ohne Kauf nach 4 Wochen mit Rabattcode zurückholen</p>
              </div>

              {/* Report-E-Mail */}
              <div 
                onClick={() => setActiveSettingCard('report-email')}
                className="p-6 border rounded-lg hover:bg-amber-50 cursor-pointer transition-all hover:shadow-md border-amber-200"
                data-testid="report-email-settings-card-main"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Mail size={24} className="text-amber-600" />
                  <h3 className="font-semibold text-lg">Report-E-Mail</h3>
                </div>
                <p className="text-sm text-gray-600">KI-Performance & Website-Analytics per E-Mail</p>
              </div>
            </div>
          </div>

          {/* Rechnungen & Zahlungen */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CreditCard size={24} className="text-green-600" />
              Rechnungen & Zahlungen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Rechnungs- & Firmendaten */}
              <div 
                onClick={() => setActiveSettingCard('invoice')}
                className="p-6 border rounded-lg hover:bg-green-50 cursor-pointer transition-all hover:shadow-md border-green-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <FileText size={24} className="text-green-700" />
                  <h3 className="font-semibold text-lg">Rechnung & Firmendaten</h3>
                </div>
                <p className="text-sm text-gray-600">Firmenadresse, MWST/UID, Bankverbindung & Standardtexte</p>
              </div>

              {/* Auto-Abrechnung */}
              <div 
                onClick={() => setActiveSettingCard('billing-auto')}
                className="p-6 border rounded-lg hover:bg-green-50 cursor-pointer transition-all hover:shadow-md border-green-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Zap size={24} className="text-green-600" />
                  <h3 className="font-semibold text-lg">Auto-Abrechnung</h3>
                </div>
                <p className="text-sm text-gray-600">Automatische monatliche Abrechnung</p>
              </div>

              {/* Zeitplan-Einstellungen */}
              <div 
                onClick={() => setActiveSettingCard('scheduler')}
                className="p-6 border rounded-lg hover:bg-blue-50 cursor-pointer transition-all hover:shadow-md border-blue-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Clock size={24} className="text-blue-600" />
                  <h3 className="font-semibold text-lg">Zeitplan</h3>
                </div>
                <p className="text-sm text-gray-600">Automatische Miet-Rechnungen & Abrechnungszeiten</p>
              </div>

              {/* Zahlungserinnerungen */}
              <div 
                onClick={() => setActiveSettingCard('reminders')}
                className="p-6 border rounded-lg hover:bg-green-50 cursor-pointer transition-all hover:shadow-md border-green-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">⏰</span>
                  <h3 className="font-semibold text-lg">Zahlungserinnerungen</h3>
                </div>
                <p className="text-sm text-gray-600">Automatische Erinnerungen für offene Rechnungen</p>
              </div>

              {/* MWST */}
              <div 
                onClick={() => setActiveSettingCard('tax')}
                className="p-6 border rounded-lg hover:bg-green-50 cursor-pointer transition-all hover:shadow-md border-green-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Package size={24} className="text-green-600" />
                  <h3 className="font-semibold text-lg">MWST</h3>
                </div>
                <p className="text-sm text-gray-600">Mehrwertsteuer-Einstellungen</p>
              </div>

              {/* KI, Kanäle & Zahlungen — Phase 3 Aufteilung von "KI & API" */}
              <div 
                onClick={() => setActiveSettingCard('ki')}
                className="p-6 border-2 rounded-lg hover:bg-purple-50 cursor-pointer transition-all hover:shadow-md border-purple-200 bg-purple-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles size={24} className="text-purple-600" />
                  <h3 className="font-semibold text-lg text-purple-700">KI</h3>
                </div>
                <p className="text-sm text-purple-600">KI-Provider, Modelle, WhatsApp-Bot & Ernährungs-KI</p>
              </div>

              <div 
                onClick={() => setActiveSettingCard('kanaele')}
                className="p-6 border-2 rounded-lg hover:bg-emerald-50 cursor-pointer transition-all hover:shadow-md border-emerald-200 bg-emerald-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <MessageCircle size={24} className="text-emerald-600" />
                  <h3 className="font-semibold text-lg text-emerald-700">Kanäle</h3>
                </div>
                <p className="text-sm text-emerald-600">E-Mail (SMTP), WhatsApp API & Push</p>
              </div>

              <div 
                onClick={() => setActiveSettingCard('zahlungen')}
                className="p-6 border-2 rounded-lg hover:bg-blue-50 cursor-pointer transition-all hover:shadow-md border-blue-200 bg-blue-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <CreditCard size={24} className="text-blue-600" />
                  <h3 className="font-semibold text-lg text-blue-700">Zahlungen</h3>
                </div>
                <p className="text-sm text-blue-600">Payrexx Zahlungs-Integration</p>
              </div>

              {/* Tarif 595 */}
              <div 
                onClick={() => setActiveSettingCard('tarif595')}
                className="p-6 border-2 rounded-lg hover:bg-teal-50 cursor-pointer transition-all hover:shadow-md border-teal-200 bg-teal-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <ClipboardList size={24} className="text-teal-600" />
                  <h3 className="font-semibold text-lg text-teal-700">Tarif 595</h3>
                </div>
                <p className="text-sm text-teal-600">Krankenkassen-Abrechnungen, GLN/ZSR, XML 5.0</p>
              </div>
            </div>
          </div>

          {/* Services & Produkte */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={24} className="text-orange-600" />
              Services & Produkte
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Service-Kategorien */}
              <div 
                onClick={() => setActiveSettingCard('categories')}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Tag size={24} className="text-blue-600" />
                  <h3 className="font-semibold text-lg">Service-Kategorien</h3>
                </div>
                <p className="text-sm text-gray-600">Kategorien für Services verwalten</p>
              </div>

              {/* Services */}
              <div 
                onClick={() => window.location.href = '/services'}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Package size={24} className="text-orange-600" />
                  <h3 className="font-semibold text-lg">Services</h3>
                </div>
                <p className="text-sm text-gray-600">Service-Verwaltung</p>
              </div>
              
              {/* Produkte */}
              <div 
                onClick={() => window.location.href = '/products'}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <CreditCard size={24} className="text-pink-600" />
                  <h3 className="font-semibold text-lg">Produkte</h3>
                </div>
                <p className="text-sm text-gray-600">Produkt-Verwaltung</p>
              </div>

              {/* Anamnese */}
              <div 
                onClick={() => setActiveSettingCard('anamnese')}
                className="p-6 border rounded-lg hover:bg-cyan-50 cursor-pointer transition-all hover:shadow-md border-cyan-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <ClipboardList size={24} className="text-cyan-600" />
                  <h3 className="font-semibold text-lg">Anamnese</h3>
                </div>
                <p className="text-sm text-gray-600">Anamnese-Fragen verwalten</p>
              </div>

              {/* Zusatzleistungen (Add-on Services) */}
              <div 
                onClick={() => setActiveSettingCard('addons')}
                className="p-6 border rounded-lg hover:bg-amber-50 cursor-pointer transition-all hover:shadow-md border-amber-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Gift size={24} className="text-amber-600" />
                  <h3 className="font-semibold text-lg">Zusatzleistungen</h3>
                </div>
                <p className="text-sm text-gray-600">Add-on Services nach Buchungen anbieten</p>
              </div>

              {/* Ernährung - Mahlzeiten-Verteilung */}
              <div 
                onClick={() => setActiveSettingCard('nutrition')}
                className="p-6 border-2 rounded-lg hover:bg-orange-50 cursor-pointer transition-all hover:shadow-md border-orange-300 bg-orange-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Utensils size={24} className="text-orange-600" />
                  <h3 className="font-semibold text-lg text-orange-700">Ernährung</h3>
                </div>
                <p className="text-sm text-orange-600">Standard-Mahlzeiten-Verteilung für alle Kunden</p>
              </div>

              {/* Kundenportal - Navigiert zur neuen Seite */}
              <div 
                onClick={() => window.location.href = '/portal-settings'}
                className="p-6 border-2 rounded-lg hover:bg-green-50 cursor-pointer transition-all hover:shadow-md border-green-300 bg-green-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Globe size={24} className="text-green-600" />
                  <h3 className="font-semibold text-lg text-green-700">Kundenportal</h3>
                </div>
                <p className="text-sm text-green-600">Portal Aussehen, KI-Assistent & Einbettung</p>
              </div>
            </div>
          </div>

          {/* Tracking & Analyse */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Utensils size={24} className="text-orange-600" />
              Tracking & Analyse
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Ernährung */}
              <div 
                onClick={() => setActiveSettingCard('nutrition')}
                className="p-6 border-2 rounded-lg hover:bg-orange-50 cursor-pointer transition-all hover:shadow-md border-orange-300 bg-orange-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Utensils size={24} className="text-orange-600" />
                  <h3 className="font-semibold text-lg text-orange-700">Ernährung</h3>
                </div>
                <p className="text-sm text-orange-600">Standard-Mahlzeiten-Verteilung für alle Kunden</p>
              </div>
            </div>
          </div>

          {/* System */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Shield size={24} className="text-red-600" />
              System & Rechtliches
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Mobile App */}
              <div 
                onClick={() => window.location.href = '/mobile-app'}
                className="p-6 border-2 border-blue-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-all hover:shadow-md bg-blue-50/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Smartphone size={24} className="text-blue-600" />
                  <h3 className="font-semibold text-lg text-blue-700">Mobile App</h3>
                </div>
                <p className="text-sm text-blue-600">App-Preview, Branding & Push-Notifications</p>
              </div>

              {/* Backup */}
              <div 
                onClick={() => setActiveSettingCard('backup')}
                className="p-6 border-2 border-green-200 rounded-lg hover:bg-green-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Database size={24} className="text-green-600" />
                  <h3 className="font-semibold text-lg text-green-700">Backup</h3>
                </div>
                <p className="text-sm text-green-600">Automatische Datensicherung per E-Mail</p>
              </div>

              {/* Policies */}
              <div 
                onClick={() => setActiveSettingCard('policies')}
                className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Shield size={24} className="text-red-600" />
                  <h3 className="font-semibold text-lg">Policies</h3>
                </div>
                <p className="text-sm text-gray-600">Datenschutz & AGB</p>
              </div>

              {/* Wartung / Maintenance */}
              <div 
                onClick={() => setActiveSettingCard('maintenance')}
                className="p-6 border-2 border-amber-200 rounded-lg hover:bg-amber-50 cursor-pointer transition-all hover:shadow-md"
                data-testid="settings-card-maintenance"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Clock size={24} className="text-amber-600" />
                  <h3 className="font-semibold text-lg text-amber-700">Wartung</h3>
                </div>
                <p className="text-sm text-amber-600">Termin-Zeitzonen normalisieren</p>
              </div>

              {/* System / Admin */}
              <div 
                onClick={() => setActiveSettingCard('system')}
                className="p-6 border-2 border-red-200 rounded-lg hover:bg-red-50 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle size={24} className="text-red-600" />
                  <h3 className="font-semibold text-lg text-red-700">System</h3>
                </div>
                <p className="text-sm text-red-600">Daten zurücksetzen & Admin</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Detail-Ansicht mit Zurück-Button
        <div>
          {/* Zurück zur Übersicht - nur anzeigen wenn NICHT in Kommunikations-Detail-Ansicht und NICHT eingebettet */}
          {!embedded && !(activeSettingCard === 'communication' && activeCommunicationTab) && (
            <Button
              variant="outline"
              onClick={() => goBackToSettingsHub()}
              className="mb-4"
            >
              ← Zurück zur Übersicht
            </Button>
          )}

          {/* Hier kommt der bestehende Tab-basierte Content */}
          <Tabs value={activeSettingCard} onValueChange={setActiveSettingCard} className="space-y-6">
            {/* Keine TabsList mehr - wird durch Karten-Clicks gesteuert */}

        {/* Kommunikation (Email + WhatsApp zusammen) */}
        <TabsContent value="communication">
          {!activeCommunicationTab ? (
            // Karten-Grid für Kommunikation
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  💬 Kommunikations-Einstellungen
                </CardTitle>
                <CardDescription>
                  Wählen Sie einen Bereich aus
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Erinnerungen Karte */}
                  <div 
                    onClick={() => setActiveCommunicationTab('reminders')}
                    className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Clock size={24} className="text-purple-600" />
                      <h3 className="font-semibold text-lg">Erinnerungen</h3>
                    </div>
                    <p className="text-sm text-gray-600">Terminerinnerungen & Auto-Archivierung</p>
                  </div>

                  {/* Geburtstage Karte */}
                  <div 
                    onClick={() => setActiveCommunicationTab('birthday')}
                    className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
                    data-testid="birthday-settings-card"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Gift size={24} className="text-pink-500" />
                      <h3 className="font-semibold text-lg">Geburtstage</h3>
                    </div>
                    <p className="text-sm text-gray-600">Lieferanten-Emails vor Kundengeburtstagen</p>
                  </div>

                  {/* Newsletter Karte */}
                  <div 
                    onClick={() => setActiveCommunicationTab('newsletter')}
                    className="p-6 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all hover:shadow-md"
                    data-testid="newsletter-settings-card"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Newspaper size={24} className="text-indigo-600" />
                      <h3 className="font-semibold text-lg">Newsletter</h3>
                    </div>
                    <p className="text-sm text-gray-600">Newsletter an Kunden versenden</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            // Detail-Ansicht
            <div>
              <Button
                variant="outline"
                onClick={() => setActiveCommunicationTab(null)}
                className="mb-4"
              >
                ← Zurück zur Kommunikation
              </Button>
              
              {/* Birthday Settings */}
              {activeCommunicationTab === 'birthday' && (
                <BirthdaySettings token={localStorage.getItem('token')} />
              )}

              {/* Warnsystem / Notification Templates */}
              {activeCommunicationTab === 'notification_templates' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="border border-yellow-300 bg-yellow-50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <span>Guthaben-Warnung (Email)</span>
                        </CardTitle>
                        <CardDescription>
                          Vorlage für automatische Warnungen bei niedrigem Punkte- oder Abo-Guthaben.
                          Bearbeiten Sie diese unter <strong>Email Vorlagen</strong> (Template-Typ wird automatisch angelegt).
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-700 mb-2">
                          Diese Vorlage steuert u.a. die Inhalte für:
                        </p>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                          <li>Warnung bei niedrigem Punktestand</li>
                          <li>Warnung bei niedrigem 10er-Abo-Guthaben (Kryosauna, EMS, PT, ...)</li>
                        </ul>
                        <p className="text-xs text-gray-500 mt-3">
                          Hinweis: Die eigentlichen Texte bearbeiten Sie unter <strong>Einstellungen → Kommunikation → Email Vorlagen</strong>.
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Schwellwerte für Warnsystem</CardTitle>
                        <CardDescription>
                          Legen Sie fest, ab welchem Restguthaben Warnungen im System angezeigt werden.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="low_points_threshold">Warnung Punktestand unter (Punkte)</Label>
                            <Input
                              id="low_points_threshold"
                              type="number"
                              min="0"
                              value={smtpSettings.low_points_threshold ?? 10}
                              onChange={(e) => setSmtpSettings({
                                ...smtpSettings,
                                low_points_threshold: parseInt(e.target.value) || 0,
                              })}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Beispiel: 10 → "Du hast nur noch X Punkte übrig" wird unterhalb 10 angezeigt.
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="low_credit_threshold">Warnung 10er-Abos unter (Sessions)</Label>
                            <Input
                              id="low_credit_threshold"
                              type="number"
                              min="0"
                              value={smtpSettings.low_credit_threshold ?? 3}
                              onChange={(e) => setSmtpSettings({
                                ...smtpSettings,
                                low_credit_threshold: parseInt(e.target.value) || 0,
                              })}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Beispiel: 3 → "Es verbleiben dir nur noch X Sessions" wird bei X ≤ 3 angezeigt.
                            </p>
                          </div>

                          <p className="text-xs text-gray-500">
                            Diese Schwellwerte steuern aktuell die Anzeigen im Kundenprofil. Später können sie
                            auch für automatische Email-/WhatsApp-Warnungen verwendet werden.
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Kreditkarten-Ablauf Einstellungen */}
                    <Card className="border border-amber-300 bg-amber-50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <span>Kreditkarten-Ablauf Benachrichtigung</span>
                        </CardTitle>
                        <CardDescription>
                          Automatische Erinnerungen wenn Kreditkarten bald ablaufen
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label>Benachrichtigungen aktiviert</Label>
                              <p className="text-xs text-gray-500">Automatische Erinnerungen senden</p>
                            </div>
                            <Switch
                              checked={smtpSettings.card_expiry_enabled ?? true}
                              onCheckedChange={(checked) => setSmtpSettings({
                                ...smtpSettings,
                                card_expiry_enabled: checked
                              })}
                            />
                          </div>
                          
                          <div>
                            <Label>Tage vor Ablauf (Erste Benachrichtigung)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="90"
                              value={smtpSettings.card_expiry_days_before ?? 30}
                              onChange={(e) => setSmtpSettings({
                                ...smtpSettings,
                                card_expiry_days_before: parseInt(e.target.value) || 30
                              })}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Standard: 30 Tage vor Ablauf
                            </p>
                          </div>
                          
                          <div>
                            <Label>Wiederholungsintervall (Tage)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="30"
                              value={smtpSettings.card_expiry_repeat_interval ?? 7}
                              onChange={(e) => setSmtpSettings({
                                ...smtpSettings,
                                card_expiry_repeat_interval: parseInt(e.target.value) || 7
                              })}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Standard: Alle 7 Tage wiederholen
                            </p>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <Label>Nur aktive Kunden</Label>
                              <p className="text-xs text-gray-500">Deaktivierte Kunden ausschliessen</p>
                            </div>
                            <Switch
                              checked={smtpSettings.card_expiry_active_only ?? true}
                              onCheckedChange={(checked) => setSmtpSettings({
                                ...smtpSettings,
                                card_expiry_active_only: checked
                              })}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <Label>Per Email senden</Label>
                            </div>
                            <Switch
                              checked={smtpSettings.card_expiry_email ?? true}
                              onCheckedChange={(checked) => setSmtpSettings({
                                ...smtpSettings,
                                card_expiry_email: checked
                              })}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <Label>Per WhatsApp senden</Label>
                            </div>
                            <Switch
                              checked={smtpSettings.card_expiry_whatsapp ?? false}
                              onCheckedChange={(checked) => setSmtpSettings({
                                ...smtpSettings,
                                card_expiry_whatsapp: checked
                              })}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button onClick={saveCommunicationSettings} disabled={loading} size="lg">
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Speichern...' : 'Einstellungen speichern'}
                    </Button>
                  </div>
                </div>
              )}
                
              {/* Terminerinnerungen */}
              {activeCommunicationTab === 'reminders' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Email Erinnerungen */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Mail className="h-5 w-5" /> Email Terminerinnerungen
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div>
                            <Label className="font-medium">Aktivieren</Label>
                            <p className="text-xs text-gray-600">Automatische Email-Erinnerungen</p>
                          </div>
                          <Switch
                            checked={smtpSettings.email_reminders_enabled || false}
                            onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, email_reminders_enabled: checked})}
                          />
                        </div>

                        {smtpSettings.email_reminders_enabled && (
                          <>
                            <div className="space-y-2">
                              <Label className="text-sm">Versand-Zeitpunkt</Label>
                              <div className="space-y-2">
                                <div
                                  onClick={() => setSmtpSettings({...smtpSettings, email_reminder_mode: 'immediate'})}
                                  className={`p-3 border-2 rounded-lg cursor-pointer ${
                                    smtpSettings.email_reminder_mode === 'immediate'
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-gray-200'
                                  }`}
                                >
                                  <p className="text-sm font-medium">Sofort bei Buchung</p>
                                </div>
                                <div
                                  onClick={() => setSmtpSettings({...smtpSettings, email_reminder_mode: 'before'})}
                                  className={`p-3 border-2 rounded-lg cursor-pointer ${
                                    smtpSettings.email_reminder_mode === 'before'
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-gray-200'
                                  }`}
                                >
                                  <p className="text-sm font-medium">X Stunden vorher</p>
                                </div>
                              </div>
                            </div>

                            {smtpSettings.email_reminder_mode === 'before' && (
                              <div className="space-y-2">
                                <Label className="text-sm">Stunden vor Termin</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="168"
                                  value={smtpSettings.email_reminder_hours || 24}
                                  onChange={(e) => setSmtpSettings({...smtpSettings, email_reminder_hours: e.target.value})}
                                  className="w-full"
                                />
                                <p className="text-xs text-gray-500">1h = 1 | 1 Tag = 24 | 2 Tage = 48</p>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {/* WhatsApp Erinnerungen */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <MessageCircle className="h-5 w-5" /> WhatsApp Terminerinnerungen
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div>
                            <Label className="font-medium">Aktivieren</Label>
                            <p className="text-xs text-gray-600">Automatische WhatsApp-Erinnerungen</p>
                          </div>
                          <Switch
                            checked={smtpSettings.whatsapp_reminders_enabled || false}
                            onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, whatsapp_reminders_enabled: checked})}
                          />
                        </div>

                        {smtpSettings.whatsapp_reminders_enabled && (
                          <>
                            <div className="space-y-2">
                              <Label className="text-sm">Versand-Zeitpunkt</Label>
                              <div className="space-y-2">
                                <div
                                  onClick={() => setSmtpSettings({...smtpSettings, whatsapp_reminder_mode: 'immediate'})}
                                  className={`p-3 border-2 rounded-lg cursor-pointer ${
                                    smtpSettings.whatsapp_reminder_mode === 'immediate'
                                      ? 'border-green-500 bg-green-50'
                                      : 'border-gray-200'
                                  }`}
                                >
                                  <p className="text-sm font-medium">Sofort bei Buchung</p>
                                </div>
                                <div
                                  onClick={() => setSmtpSettings({...smtpSettings, whatsapp_reminder_mode: 'before'})}
                                  className={`p-3 border-2 rounded-lg cursor-pointer ${
                                    smtpSettings.whatsapp_reminder_mode === 'before'
                                      ? 'border-green-500 bg-green-50'
                                      : 'border-gray-200'
                                  }`}
                                >
                                  <p className="text-sm font-medium">X Stunden vorher</p>
                                </div>
                              </div>
                            </div>

                            {smtpSettings.whatsapp_reminder_mode === 'before' && (
                              <div className="space-y-2">
                                <Label className="text-sm">Stunden vor Termin</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="168"
                                  value={smtpSettings.whatsapp_reminder_hours || 24}
                                  onChange={(e) => setSmtpSettings({...smtpSettings, whatsapp_reminder_hours: e.target.value})}
                                  className="w-full"
                                />
                                <p className="text-xs text-gray-500">1h = 1 | 1 Tag = 24 | 2 Tage = 48</p>
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label className="text-sm">WhatsApp Template auswählen *</Label>
                              <WhatsAppTemplateSelect
                                templates={whatsappTemplates}
                                value={smtpSettings.whatsapp_template || ''}
                                onValueChange={(name) => setSmtpSettings({...smtpSettings, whatsapp_template: name})}
                                testId="reminder-whatsapp-template-select"
                              />
                              <p className="text-xs text-gray-500 flex items-start gap-1">
                                <Globe className="h-3 w-3 mt-0.5 shrink-0 text-green-600" />
                                <span>Wird automatisch in der Kundensprache gesendet, wenn die Sprachvariante bei Meta genehmigt ist – sonst auf Deutsch.</span>
                              </p>
                              {whatsappTemplates.length === 0 && (
                                <p className="text-xs text-gray-500">
                                  Keine genehmigten Templates. Bitte zuerst Templates erstellen.
                                </p>
                              )}
                              
                              {smtpSettings.whatsapp_template && whatsappTemplates.find(t => t.name === smtpSettings.whatsapp_template) && (
                                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                  <p className="text-xs text-green-900 font-medium mb-2">
                                    Template-Variablen:
                                  </p>
                                  <div className="text-xs text-green-800 space-y-1">
                                    <p>• {`{{1}}`} = Kundenname (Vorname + Nachname)</p>
                                    <p>• {`{{2}}`} = Termin-Datum (TT.MM.JJJJ)</p>
                                    <p>• {`{{3}}`} = Termin-Zeit (HH:MM Uhr)</p>
                                    <p>• {`{{4}}`} = Service-Name</p>
                                  </div>
                                  <p className="text-xs text-green-700 mt-2">
                                    Diese Werte werden automatisch aus dem Termin eingefügt
                                  </p>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Punkte-Warnung */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        Punkte-Warnung
                      </CardTitle>
                      <CardDescription>
                        Automatische Benachrichtigung bei niedrigem Punkte-Guthaben
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <Label className="font-medium">Aktivieren</Label>
                          <p className="text-xs text-gray-600">Warnung bei niedrigem Guthaben</p>
                        </div>
                        <Switch
                          checked={smtpSettings.points_warning_enabled || false}
                          onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, points_warning_enabled: checked})}
                        />
                      </div>

                      {smtpSettings.points_warning_enabled && (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-sm">Warnung ab</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="100000"
                                value={smtpSettings.points_warning_threshold || 10}
                                onChange={(e) => setSmtpSettings({...smtpSettings, points_warning_threshold: e.target.value})}
                                className="w-32"
                              />
                              <span className="text-sm text-gray-600">Punkten</span>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Abstand zwischen Anschreiben</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="90"
                                value={smtpSettings.points_warning_frequency_days ?? 7}
                                onChange={(e) => setSmtpSettings({...smtpSettings, points_warning_frequency_days: e.target.value})}
                                className="w-32"
                                data-testid="points-warning-frequency-input"
                              />
                              <span className="text-sm text-gray-600">Tagen</span>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Maximale Anzahl Anschreiben</Label>
                            <p className="text-xs text-gray-600">
                              Danach ist Schluss, bis das Guthaben wieder aufgefüllt ist. 0 = unbegrenzt.
                            </p>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="10"
                                value={smtpSettings.points_warning_max_count ?? 2}
                                onChange={(e) => setSmtpSettings({...smtpSettings, points_warning_max_count: e.target.value})}
                                className="w-32"
                                data-testid="points-warning-max-count-input"
                              />
                              <span className="text-sm text-gray-600">mal</span>
                            </div>
                          </div>

                          <div className="pt-3 border-t space-y-2">
                            <Label className="text-sm">Warnung für 10er-Abos</Label>
                            <p className="text-xs text-gray-600">
                              Gilt für Kredit-Abos wie Kryosauna, EMS, PT usw.
                            </p>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                value={smtpSettings.credit_warning_threshold || 3}
                                onChange={(e) => setSmtpSettings({...smtpSettings, credit_warning_threshold: e.target.value})}
                                className="w-32"
                              />
                              <span className="text-sm text-gray-600">Sessions</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Auto-Archivierung WhatsApp */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        Auto-Archivierung WhatsApp
                      </CardTitle>
                      <CardDescription>
                        Chats automatisch als erledigt markieren nach X Stunden ohne Antwort
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="font-medium">Auto-Archivierung aktivieren</Label>
                          <p className="text-xs text-gray-600">Chats ohne Kunden-Antwort automatisch erledigen</p>
                        </div>
                        <Switch
                          checked={smtpSettings.auto_archive_enabled !== false}
                          onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, auto_archive_enabled: checked})}
                        />
                      </div>

                      {smtpSettings.auto_archive_enabled !== false && (
                        <div className="space-y-2">
                          <Label className="text-sm">Archivieren nach</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="168"
                              value={smtpSettings.auto_archive_hours || 24}
                              onChange={(e) => setSmtpSettings({...smtpSettings, auto_archive_hours: e.target.value})}
                              className="w-32"
                            />
                            <span className="text-sm text-gray-600">Stunden ohne Kunden-Antwort</span>
                          </div>
                          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mt-3">
                            <p className="text-xs text-blue-900">
                              <strong>Hinweis:</strong> Wenn ein Kunde antwortet, wird der Chat automatisch wieder aktiviert.
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  <div className="flex justify-end">
                    <Button onClick={saveCommunicationSettings} disabled={loading} size="lg">
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Speichern...' : 'Alle Einstellungen speichern'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Newsletter */}
              {activeCommunicationTab === 'newsletter' && (
                <NewsletterTab />
              )}
            </div>
          )}
        </TabsContent>

        {/* Automatische Abrechnung */}
        <TabsContent value="billing-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                ⚡ Automatische Abrechnung
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie die automatische monatliche Abrechnung für &quot;Ende Monat&quot; Kunden
                sowie die tägliche Auto-Abrechnung für Kunden mit Zahlungsmodus &quot;täglich&quot;.
              </CardDescription>
              <div className="mt-2 text-xs text-gray-500 space-y-1">
                <p>
                  • <strong>Ende Monat</strong>: Kunden mit Zahlungsmodus <strong>&quot;ende_monat&quot;</strong> werden nur
                  am gewählten Tag des Monats (oben) abgerechnet.
                </p>
                <p>
                  • <strong>Täglich</strong>: Kunden mit Zahlungsmodus <strong>&quot;täglich&quot;</strong> werden über den
                  Hintergrund-Scheduler in dem unten gewählten Intervall (z.B. alle 60 Minuten) abgerechnet.
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                <div className="flex-1">
                  <h4 className="font-semibold text-lg">Automatische Abrechnung aktivieren</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Wenn aktiviert, werden alle &quot;Ende Monat&quot; Kunden automatisch am gewählten Tag abgerechnet
                  </p>
                </div>
                <Switch
                  checked={billingAutomation.enabled}
                  onCheckedChange={(checked) => setBillingAutomation({...billingAutomation, enabled: checked})}
                />
              </div>

              {/* 08/2026: Rechnungsversand mit Zahlungslink */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                <div className="flex-1">
                  <h4 className="font-semibold text-lg">Rechnung per E-Mail mit Zahlungslink senden</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Kunden ohne automatische Kartenbelastung (oder bei fehlgeschlagener Belastung) erhalten
                    die Rechnung automatisch per E-Mail mit Payrexx-Zahlungslink (Twint/Karte)
                  </p>
                </div>
                <Switch
                  checked={billingAutomation.send_invoice_email ?? true}
                  onCheckedChange={(checked) => setBillingAutomation({...billingAutomation, send_invoice_email: checked})}
                  data-testid="billing-send-invoice-email-switch"
                />
              </div>

              {/* Billing Day */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Tag des Monats für Abrechnung</Label>
                  <Select
                    value={billingAutomation.billing_day.toString()}
                    onValueChange={(value) => setBillingAutomation({...billingAutomation, billing_day: value === 'last' ? 'last' : parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="last">Letzter Tag des Monats</SelectItem>
                      {[...Array(31)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {i + 1}. Tag des Monats
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Die Abrechnung erfolgt automatisch am gewählten Tag jeden Monats (z.B. 30. = am 30. jeden Monats)
                  </p>
                </div>

                {/* Interval */}
                <div className="space-y-2">
                  <Label>Intervall für tägliche Auto-Abrechnung</Label>
                  <Select
                    value={billingAutomation.interval_minutes.toString()}
                    onValueChange={(value) => setBillingAutomation({
                      ...billingAutomation,
                      interval_minutes: parseInt(value)
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="60">Alle 60 Minuten</SelectItem>
                      <SelectItem value="120">Alle 2 Stunden</SelectItem>
                      <SelectItem value="180">Alle 3 Stunden</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Gilt nur für Kunden mit Zahlungsmodus &quot;täglich&quot;. Ende-Monat-Kunden werden weiterhin
                    nur am gewählten Tag des Monats (oben) abgerechnet.
                  </p>
                </div>
              </div>

              {/* Last Run */}
              {billingAutomation.last_run && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Letzte Ausführung:</strong>{' '}
                    {new Date(billingAutomation.last_run).toLocaleString('de-DE')}
                  </p>
                </div>
              )}

              {/* Save & Control Buttons */}
              <div className="flex flex-col md:flex-row gap-3">
                <Button onClick={saveBillingAutomationSettings} className="flex-1">
                  <Save className="mr-2 h-4 w-4" />
                  Einstellungen speichern
                </Button>
                <Button onClick={restartBillingScheduler} variant="outline" className="flex-1">
                  🔁 Hintergrund-Automation (Cron) konfigurieren
                </Button>
                <Button onClick={runBillingNow} variant="outline" className="flex-1" disabled={loadingBillingPreview} data-testid="billing-run-now-btn">
                  {loadingBillingPreview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : '⚡ '}
                  Jetzt manuell ausführen (mit Vorschau)
                </Button>
                <Button onClick={runNightlyNow} variant="outline" className="flex-1" disabled={runningBilling} data-testid="billing-run-nightly-btn">
                  {runningBilling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : '🌙 '}
                  Termin-Abrechnung jetzt starten (Nachtlauf)
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                „Nachtlauf" rechnet auch <b>sofort-/Nachtlauf-Kunden</b> und <b>Zahler-Beziehungen</b> ab (z.B. zurückgesetzte Termine wie Dennis → Sandra). Die Vorschau oben zeigt nur Ende-Monat-/Täglich-Kunden.
              </p>

              {/* 08/2026: Abrechnungs-Vorschau Dialog */}
              <Dialog open={showBillingPreview} onOpenChange={setShowBillingPreview}>
                <DialogContent className="max-w-2xl" data-testid="billing-preview-dialog">
                  <DialogHeader>
                    <DialogTitle>Abrechnungs-Vorschau</DialogTitle>
                    <DialogDescription>
                      Diese Kunden würden beim Lauf jetzt verrechnet (vergangene, offene Termine).
                    </DialogDescription>
                  </DialogHeader>
                  {billingPreview && (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {billingPreview.customer_count === 0 && (
                        <p className="text-sm text-gray-500" data-testid="billing-preview-empty">
                          Keine offenen Beträge — es würde niemand verrechnet.
                        </p>
                      )}
                      {['ende_monat', 'taeglich'].map((timing) => (
                        (billingPreview[timing] || []).length > 0 && (
                          <div key={timing}>
                            <h4 className="font-semibold text-sm mb-2">
                              {timing === 'ende_monat' ? '📅 Ende Monat' : '🔁 Tägliche Abrechnung'}
                            </h4>
                            <div className="border rounded-lg divide-y">
                              {billingPreview[timing].map((c) => (
                                <div key={c.customer_id} className="flex items-center justify-between p-2 text-sm" data-testid={`billing-preview-row-${c.customer_id}`}>
                                  <span>
                                    {c.name}
                                    {c.family_names?.length > 0 && (
                                      <span className="block text-xs text-blue-600" data-testid={`billing-preview-family-${c.customer_id}`}>
                                        inkl. Angehörige: {c.family_names.join(', ')}
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-gray-500">{c.appointment_count} Termin{c.appointment_count !== 1 ? 'e' : ''}</span>
                                  <span className="font-semibold">CHF {Number(c.total_amount).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                      {billingPreview.customer_count > 0 && (
                        <div className="flex items-center justify-between border-t pt-3 font-bold">
                          <span>Total ({billingPreview.customer_count} Kunde{billingPreview.customer_count !== 1 ? 'n' : ''})</span>
                          <span data-testid="billing-preview-total">CHF {Number(billingPreview.total_amount).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowBillingPreview(false)} data-testid="billing-preview-cancel-btn">
                      Abbrechen
                    </Button>
                    <Button
                      onClick={confirmBillingRun}
                      disabled={runningBilling || !billingPreview || billingPreview.customer_count === 0}
                      data-testid="billing-preview-confirm-btn"
                    >
                      {runningBilling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Jetzt abrechnen{billingPreview?.customer_count > 0 ? ` (CHF ${Number(billingPreview.total_amount).toFixed(2)})` : ''}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Logs */}
              {billingLogs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-lg">Letzte Ausführungen</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {billingLogs.map((log, idx) => (
                      <div key={idx} className="p-3 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            {new Date(log.timestamp).toLocaleString('de-DE')}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            (log.error_count ?? 0) === 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {log.success_count ?? log.invoices_created ?? 0} erfolgreich, {log.error_count ?? 0} Fehler
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          {log.customers_processed ?? log.processed_count ?? 0} Kunden verarbeitet
                        </p>
                        {Array.isArray(log.errors) && log.errors.length > 0 && (
                          <div className="mt-2 space-y-1" data-testid={`billing-log-errors-${idx}`}>
                            {log.errors.map((err, eIdx) => (
                              <p key={eIdx} className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">
                                ⚠️ {err.customer_name || err.customer_id}: {err.error}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Zeitplan-Einstellungen */}
        <TabsContent value="scheduler">
          <SchedulerSettingsCard />
        </TabsContent>

        {/* Chatbot Slot-Reservation */}
        <TabsContent value="slot-reservation">
          <ReservationSettings />
        </TabsContent>


        {/* Probetraining Follow-Up */}
        <TabsContent value="trial-followup">
          <TrialFollowupSettings />
        </TabsContent>

        {/* Zahlungserinnerungen */}
        <TabsContent value="reminders">
          <PaymentRemindersSettings />
        </TabsContent>

        {/* Foto-Scanner */}
        <TabsContent value="scanner">
          <ScannerSettings />
        </TabsContent>

        {/* Terminkalender Einstellungen */}
        <TabsContent value="calendar">
          <CalendarSettings />
        </TabsContent>

        {/* Öffnungszeiten & Verfügbarkeit (inkl. Kalender-Farben im Tab "Kalender-Einstellungen") */}
        <TabsContent value="availability">
          <AvailabilitySettings />
        </TabsContent>

        {/* Services */}
        <TabsContent value="services">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package size={24} />
                Dienstleistungen
              </CardTitle>
              <CardDescription>
                Services und Kurse werden auf der Services-Seite verwaltet
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-12">
                <Button onClick={() => window.location.href = '/services'}>
                  Zur Services-Verwaltung
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* KI & API Settings — Phase 3: aufgeteilt in KI / Kanäle / Zahlungen */}
        <TabsContent value="ki">
          <AIApiSettings group="ki" />
        </TabsContent>
        <TabsContent value="kanaele">
          <AIApiSettings group="kanaele" />
        </TabsContent>
        <TabsContent value="zahlungen">
          <AIApiSettings group="zahlungen" />
        </TabsContent>

        {/* Policies - AGB & Datenschutz */}
        <TabsContent value="policies">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Shield size={24} />
                  Datenschutz & AGB
                </h2>
                <p className="text-gray-500">Verwalten Sie rechtliche Dokumente und Kunden-Akzeptierungen</p>
              </div>
              <Button variant="outline" onClick={() => goBackToSettingsHub()}>
                ← Zurück
              </Button>
            </div>
            
            {/* Legal Settings Card with URLs and Reset */}
            <LegalSettingsCard />
            
            {/* Editor Links */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Dokumente bearbeiten</CardTitle>
                <CardDescription>Öffnen Sie den Editor um Inhalte anzupassen</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => window.location.href = '/policies/editor'}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Datenschutzerklärung bearbeiten
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => window.location.href = '/policies/editor'}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  AGB bearbeiten
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Ernährung - Mahlzeiten-Verteilung */}
        <TabsContent value="nutrition">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Utensils className="w-6 h-6 text-orange-600" />
                  Ernährungs-Einstellungen
                </h2>
                <p className="text-gray-600 mt-1">
                  Kalorien-Berechnung, Mahlzeiten-Verteilung und Standard-Einstellungen
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => goBackToSettingsHub()}
              >
                ← Zurück
              </Button>
            </div>
            
            {/* Kalorien-Berechnung Einstellungen (inkl. Mahlzeiten-Verteilung im eigenen Tab) */}
            <NutritionCalculationSettings />
          </div>
        </TabsContent>

        {/* InBody Einstellungen */}
        <TabsContent value="inbody">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Scan className="w-6 h-6 text-blue-600" />
                  InBody Einstellungen
                </h2>
                <p className="text-gray-600 mt-1">
                  Körperanalyse-Sichtbarkeit für Kunden konfigurieren
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => goBackToSettingsHub()}
              >
                ← Zurück
              </Button>
            </div>
            
            <InBodySettingsCard />
          </div>
        </TabsContent>

        {/* Allgemein */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon size={24} />
                Allgemeine Einstellungen
              </CardTitle>
              <CardDescription>
                Studio-Informationen und allgemeine Konfiguration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <GeneralSettingsForm />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automatisierung - Separate Seite ohne Tabs */}
        <TabsContent value="automation">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    ⚡ Automatische Benachrichtigungen
                  </CardTitle>
                  <CardDescription>
                    Legen Sie fest, wann welche Email- und WhatsApp-Nachrichten automatisch versendet werden
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={() => goBackToSettingsHub()}
                >
                  ← Zurück zur Übersicht
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hier kommen alle Automatisierungsregeln aus dem Tab "automation" in communication */}
              {/* Ich kopiere sie hierher */}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Kategorien */}
        <TabsContent value="categories">
          <CategoriesSettings />
        </TabsContent>

        {/* RECHNUNG & FIRMENDATEN */}
        <TabsContent value="invoice">
          <InvoiceSettings />
        </TabsContent>

        {/* Report-E-Mail Einstellungen */}
        <TabsContent value="report-email">
          <ReportEmailSettings />
        </TabsContent>

        {/* MWST Einstellungen */}

        {/* MWST-Einstellungen */}
        <TabsContent value="tax">
          <TaxSettings />
        </TabsContent>

        {/* Google-Bewertung (Marketing) — globale Automatik; Bewertungslink pro Studio in der Studio-Verwaltung */}
        <TabsContent value="google-review">
          <ReviewAutomationCard />
        </TabsContent>

        {/* Termin-Feedback — Häufigkeit der In-App-Feedback-Frage */}
        <TabsContent value="termin-feedback">
          <FeedbackSettingsCard />
        </TabsContent>


        {/* System / Admin - Danger Zone */}
        <TabsContent value="system">
          <SystemSettings />
        </TabsContent>

        {/* Anamnese-Einstellungen */}
        <TabsContent value="anamnese">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-cyan-600" />
                    Anamnese-Einstellungen
                  </CardTitle>
                  <CardDescription>
                    Verwalten Sie die Fragen für den Gesundheitsfragebogen
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={() => goBackToSettingsHub()}
                >
                  ← Zurück
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <AnamneseSettings />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Zusatzleistungen (Add-on Services) */}
        <TabsContent value="addons">
          <AddonsSettings />
        </TabsContent>

        {/* Kundenportal Einstellungen */}
        <TabsContent value="portal">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-green-600" />
                      Kundenportal Einstellungen
                    </CardTitle>
                    <CardDescription>
                      Passen Sie Aussehen und Funktionen des Kundenportals an
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => goBackToSettingsHub()}
                  >
                    ← Zurück
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <PortalSettings />
              </CardContent>
            </Card>
            
            {/* AGB & Datenschutz Settings */}
            <LegalSettingsCard />
          </div>
        </TabsContent>

        {/* Backup */}
        <TabsContent value="backup" className="space-y-6">
          <AdminBackups />
          <PeriodExportCard />
          <BackupSettings />
          <SettingsSyncCard />
        </TabsContent>

        {/* Wartung / Maintenance */}
        <TabsContent value="maintenance" className="space-y-6">
          <MaintenanceSettings />
        </TabsContent>

        <TabsContent value="tarif595">
          <Tarif595Settings token={localStorage.getItem('token')} />
        </TabsContent>


      </Tabs>
        </div>
      )}
      
      {/* Version Info - immer sichtbar (außer eingebettet) */}
      {!embedded && (
        <div className="mt-8 text-center text-sm text-gray-500 border-t pt-4">
          <p data-testid="settings-version-line">{businessName} · Version {APP_VERSION}</p>
          <p className="text-xs mt-1" data-testid="settings-copyright-line">© {new Date().getFullYear()} {businessName} - Alle Rechte vorbehalten</p>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
